import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Diamond } from "lucide-react";
import { toast } from "sonner";
import { capacitacionPublicApi, apiError } from "../lib/api";
import { ModuleReader } from "../components/capacitaciones/ModuleReader";
import { QuizBox } from "../components/capacitaciones/QuizBox";
import { ModuleGrid, ProgressHeader } from "../components/capacitaciones/ModuleGrid";

const KEY = "supera_capac_publica";
const loadLocal = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };

const Gate = ({ onStart, programas, initial }) => {
  const [nombre, setNombre] = useState(initial.nombre || "");
  const [email, setEmail] = useState(initial.email || "");
  const [referencia, setReferencia] = useState("");
  const [programa, setPrograma] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ok = nombre.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && referencia.trim() && programa;
  return (
    <form data-testid="cap-public-gate" onSubmit={async (e) => {
      e.preventDefault();
      if (!ok || busy) return;
      setBusy(true); setErr("");
      const msg = await onStart({ nombre: nombre.trim(), email: email.trim(), referencia: referencia.trim(), programa });
      if (msg) setErr(msg);
      setBusy(false);
    }}
      className="mt-10 max-w-md rounded-3xl bg-white p-6 sm:p-8 space-y-4">
      <div className="text-lg font-extrabold text-[#0A1C30]">Antes de comenzar</div>
      <p className="text-sm text-[#4A5568]">Escribe la <b>Referencia Supera</b> y el <b>programa</b> exactamente como te los dio el administrador.</p>
      <input data-testid="cap-public-name" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre completo" className="w-full rounded-xl ring-1 ring-black/10 px-4 py-3 text-sm outline-none focus:ring-[#B99B2E]" />
      <input data-testid="cap-public-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo electrónico" className="w-full rounded-xl ring-1 ring-black/10 px-4 py-3 text-sm outline-none focus:ring-[#B99B2E]" />
      <input data-testid="cap-public-referencia" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Referencia Supera" className="w-full rounded-xl ring-1 ring-black/10 px-4 py-3 text-sm outline-none focus:ring-[#B99B2E]" />
      <select data-testid="cap-public-programa" value={programa} onChange={(e) => setPrograma(e.target.value)} className="w-full rounded-xl ring-1 ring-black/10 px-4 py-3 text-sm outline-none focus:ring-[#B99B2E] bg-white">
        <option value="">Selecciona tu programa</option>
        {programas.map((p) => <option key={p}>{p}</option>)}
      </select>
      {err && <div data-testid="cap-public-gate-error" className="rounded-xl bg-[#FDECEC] text-[#842029] text-sm font-semibold px-4 py-3">{err}</div>}
      <button data-testid="cap-public-start" disabled={!ok || busy} className="w-full rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Comenzar capacitación</button>
    </form>
  );
};

export default function CapacitacionPublica() {
  const { token = "general" } = useParams();
  const [data, setData] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [local, setLocal] = useState(loadLocal);
  const [cap, setCap] = useState(null);
  const save = (next) => { setLocal(next); localStorage.setItem(KEY, JSON.stringify(next)); };

  const access = async (p) => {
    try {
      const d = await capacitacionPublicApi.acceso(token, { nombre: p.nombre, email: p.email, referencia: p.referencia, programa: p.programa });
      setItems(d.items);
      return "";
    } catch (e) { return apiError(e, "No pudimos validar tus datos."); }
  };

  useEffect(() => {
    capacitacionPublicApi.info(token).then(async (d) => {
      const saved = loadLocal();
      if (saved.referencia && await access(saved)) save({ nombre: saved.nombre, email: saved.email });
      setData(d);
    }).catch((e) => setError(apiError(e, "Enlace no válido.")));
    /* eslint-disable-next-line */
  }, [token]);
  const start = async (p) => {
    const msg = await access(p);
    if (!msg) save({ ...p, aprobados: local.email === p.email ? (local.aprobados || []) : [] });
    return msg;
  };
  const changeData = () => { setItems(null); save({ nombre: local.nombre, email: local.email }); };
  const passed = local.aprobados || [];

  if (error) return <div className="min-h-screen bg-navy flex items-center justify-center text-white font-bold px-6 text-center" data-testid="cap-public-error">{error}</div>;
  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-navy relative overflow-hidden" data-testid="cap-public-page">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center gap-2 mb-10">
          {cap ? <button data-testid="cap-public-back" onClick={() => setCap(null)} className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Módulos</button>
            : <><Diamond className="w-5 h-5 text-gold fill-[var(--gold)]" /><span className="font-extrabold tracking-[0.3em] text-white">SUPERA</span></>}
          {items && <span className="ml-auto text-xs text-white/60" data-testid="cap-public-who">{local.nombre} · {local.programa} · Ref. {local.referencia}
            <button data-testid="cap-public-change" onClick={changeData} className="ml-3 underline hover:text-white">Cambiar datos</button></span>}
        </div>
        {cap ? (
          <div className="rounded-3xl bg-white p-5 sm:p-8 lg:p-10">
            <ModuleReader cap={cap} />
            <QuizBox key={cap.id} preguntas={cap.preguntas} aprobadoPrevio={passed.includes(cap.id)}
              onSubmit={(r) => capacitacionPublicApi.evaluar(token, cap.id, { nombre: local.nombre, email: local.email, referencia: local.referencia, programa: local.programa, respuestas: r })}
              onPassed={() => !passed.includes(cap.id) && save({ ...local, aprobados: [...passed, cap.id] })} />
          </div>
        ) : (
          <>
            <ProgressHeader aprobadas={(items || []).filter((c) => passed.includes(c.id)).length} total={(items || []).length} title="Capacitación Supera"
              subtitle="Conoce cada herramienta del programa de liderazgo Supera y pon a prueba lo aprendido." />
            {!items ? <Gate programas={data.programas || []} initial={local} onStart={start} />
              : <ModuleGrid items={items} isDone={(c) => passed.includes(c.id)} onOpen={(c) => { setCap(c); window.scrollTo({ top: 0 }); toast.dismiss(); }} />}
          </>
        )}
      </div>
    </div>
  );
}
